import { useCallback, useEffect, useState } from 'react';

import { clientApi } from '@/config/axios';
import { LabReview } from '@/features/labs';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export type RecordView = {
  version: number;
  hash: string;
  patient_id: string | null;
  patient_name: string | null;
  patient_dob: string | null;
  patient_mrn: string | null;
  document_id: string;
  revision_id: string;
  status: string;
  acknowledged: boolean;
  patient_scope_blocked: boolean;
  facts: Fact[];
  counts: Record<string, number>;
  coverage: { total_pages: number; total_chunks: number; complete_chunks: number };
  chunks: { id: string; page: number; status: string; reason: string | null; text: string; decision?: Decision }[];
  labs: { status: string; resolved: boolean; results_count: number };
  lab_decision?: Decision;
};
type Decision = { status: string; note: string };
type Fact = {
  id: string;
  category: string;
  value: string;
  quote: string;
  page: number;
  verified: boolean;
  manual?: boolean;
  correction_reason?: string;
  evidence_pages?: number[];
  current: string;
  comparison: string;
  decision?: Decision;
};
type Source = { documentId: string; revisionId?: string; pageNumber?: number };
const human = (s: string) => s.replaceAll('_', ' ');
const causes: Record<string, string> = {
  processing_interrupted: 'Processing was interrupted. Retry this section; saved verified findings are retained.',
  schema_failure:
    'The model response did not match the extraction structure. Retry processing or defer for manual review.',
  model_timeout:
    'The model exceeded its processing time limit. Retry this section; no clinical finding needs confirmation.',
  model_or_schema_failure:
    'The model did not return a valid complete response. This is a processing failure, not a clinical finding to confirm.',
  output_limit:
    'This section exceeded the output limit. Review the source for omitted facts; defer for manual completion if retry cannot finish.',
  unreadable_source:
    'The saved text was marked unreadable. Compare the original page with OCR; use a clearer source or a documented manual correction.',
  evidence_mismatch:
    'One or more proposed statements did not match the saved text. Inspect the candidate and source; correct the statement or defer this section with a note.',
};

export function RecordReview({
  base,
  runId,
  onSource,
  onChanged,
}: {
  base: string;
  runId: string;
  onSource: (s: Source) => void;
  onChanged: () => void;
}) {
  const [data, setData] = useState<RecordView | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const [receipt, setReceipt] = useState('');
  const [edits, setEdits] = useState<Record<string, { value: string; reason: string }>>({});
  const url = `${base}/runs/${runId}/record`;
  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const response = await clientApi.get<RecordView>(url, { signal });
        if (!signal?.aborted) {
          setData(response.data);
          setError('');
          setReviewed(false);
          setChoices({});
          setEdits({});
        }
      } catch {
        if (!signal?.aborted)
          setError(
            'Record preparation is pending or unavailable. Refresh to check its progress; no review has been submitted.',
          );
      }
    },
    [url],
  );
  useEffect(() => {
    const abort = new AbortController();
    void refresh(abort.signal);
    return () => abort.abort();
  }, [refresh]);
  const processing = !data || ['dispatching', 'queued', 'running'].includes(data.status);
  useEffect(() => {
    if (!processing) return;
    const abort = new AbortController();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh(abort.signal);
    }, 10000);
    return () => {
      abort.abort();
      window.clearInterval(timer);
    };
  }, [processing, refresh]);
  const selection = (choice: string) =>
    Object.entries(choices)
      .filter(([, c]) => c === choice)
      .map(([id]) => id);
  async function submit(acknowledge = false) {
    if (!data) return;
    setBusy(true);
    setError('');
    try {
      const { data: result } = await clientApi.post<{
        saved: number;
        remaining: number;
        deferred: number;
        acknowledged: boolean;
      }>(`${url}/decisions`, {
        note,
        reviewed,
        expected_hash: data.hash,
        defer: selection('defer'),
        patient_id: data.patient_id,
        identity_reviewed: reviewed,
        accept: selection('accept'),
        reject: selection('reject'),
        expected_version: data.version,
        request_id: crypto.randomUUID(),
        acknowledge_document: acknowledge,
      });
      setReceipt(
        `${result.saved} source statements saved to History and referral records. ${result.remaining} items unresolved. ${result.deferred} follow-ups deferred to the patient checklist.${result.acknowledged ? ' Document review acknowledged.' : ''}`,
      );
      await refresh();
      onChanged();
    } catch {
      setError(
        'Review was not confirmed. Refresh and check the current patient, source, chart and remaining sections before retrying.',
      );
    } finally {
      setBusy(false);
    }
  }
  async function retry() {
    if (!data) return;
    setBusy(true);
    setError('');
    try {
      await clientApi.post(`${url}/retry`, { expected_hash: data.hash, expected_version: data.version });
      await refresh();
      onChanged();
    } catch {
      setError(
        'Retry could not start. The source or budget may have changed, or this section requires manual review after its retry limit. Refresh before retrying.',
      );
    } finally {
      setBusy(false);
    }
  }
  const [manualCategory, setManualCategory] = useState('medical_history');
  async function correct(fact: Pick<Fact, 'id'>, category?: string) {
    if (!data || !edits[fact.id]) return;
    setBusy(true);
    setError('');
    try {
      await clientApi.post(`${url}/revisions`, {
        fact_id: fact.id,
        expected_hash: data.hash,
        expected_version: data.version,
        ...(category ? { category } : {}),
        ...edits[fact.id],
      });
      await refresh();
      onChanged();
    } catch {
      setError('Correction was not saved. Refresh the review and check the source again.');
    } finally {
      setBusy(false);
    }
  }
  function choose(id: string, disabled = false, allowAccept = true) {
    return (
      <select
        value={choices[id] || ''}
        aria-label={`Decision for ${id}`}
        className="rounded-md border bg-background p-2"
        disabled={disabled || busy || processing || !!data?.acknowledged}
        onChange={(e) => {
          setChoices((old) => ({ ...old, [id]: e.target.value }));
          setReviewed(false);
        }}>
        <option value="">Choose a decision</option>
        {allowAccept && <option value="accept">Accept source statement</option>}
        <option value="reject">Reject with reason</option>
        <option value="defer">Defer to patient checklist</option>
      </select>
    );
  }
  return (
    <section className="flex flex-col gap-4" aria-label="Comprehensive record review">
      <div className="flex flex-wrap gap-2">
        <Button disabled={busy} variant="outline" onClick={() => void refresh()}>
          Refresh record review
        </Button>
        {data?.chunks.some((c) => c.status === 'failed' || c.status === 'running') && (
          <Button variant="outline" disabled={busy || processing} onClick={() => void retry()}>
            Retry incomplete sections
          </Button>
        )}
      </div>
      {error && <p role="alert">{error}</p>}
      {receipt && <p role="status">{receipt}</p>}
      {data && (
        <>
          <p>
            {data.coverage.complete_chunks}/{data.coverage.total_chunks} sections analyzed across{' '}
            {data.coverage.total_pages} pages. Analysis coverage does not guarantee that every fact was found.
          </p>
          <p>
            Chart: {data.patient_name || 'Patient not assigned'} · DOB {data.patient_dob || 'not recorded'} · MRN{' '}
            {data.patient_mrn || 'not recorded'}
          </p>
          {!data.patient_id && (
            <p>Assign the source to the correct patient, then prepare a new review before importing any statements.</p>
          )}
          {data.patient_scope_blocked && (
            <p role="alert">
              Patient identity is ambiguous or the packet contains multiple patients. Split or correct the source
              association before accepting clinical changes.
            </p>
          )}
          {processing && (
            <p role="status">
              Preparing the next section. Verified checkpoints are retained; review controls become available when
              processing stops.
            </p>
          )}
          {Object.entries(data.counts).map(([category, count]) => (
            <Card key={category}>
              <CardHeader>
                <CardTitle>
                  {human(category)} · {count}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {data.facts
                  .filter((f) => f.category === category)
                  .map((fact) => (
                    <section key={fact.id} className="flex flex-col gap-3 border-b pb-4">
                      <div className="grid gap-3 lg:grid-cols-2">
                        <div>
                          <strong>Proposed source statement</strong>
                          <p className="whitespace-pre-wrap">{fact.value}</p>
                          <p>
                            {human(fact.comparison)}
                            {fact.manual ? ' · reviewer correction' : ''}
                            {fact.evidence_pages && fact.evidence_pages.length > 1
                              ? ` · repeated on pages ${fact.evidence_pages.join(', ')}`
                              : ''}
                          </p>
                        </div>
                        <div>
                          <strong>Current chart</strong>
                          <p className="whitespace-pre-wrap">{fact.current || 'No value in this field'}</p>
                          {fact.comparison === 'conflict' && (
                            <p>The existing value will be preserved; acceptance adds an attributed source statement.</p>
                          )}
                        </div>
                      </div>
                      <blockquote className="whitespace-pre-wrap">
                        Page {fact.page}: {fact.quote}
                      </blockquote>
                      <Button
                        variant="outline"
                        onClick={() =>
                          onSource({
                            pageNumber: fact.page,
                            documentId: data.document_id,
                            revisionId: data.revision_id,
                          })
                        }>
                        Open page {fact.page}
                      </Button>
                      {!fact.verified && (
                        <p role="alert">
                          Unverified candidate. Correct it against the original source before acceptance, or
                          reject/defer it.
                        </p>
                      )}
                      {fact.decision ? (
                        <p>
                          {human(fact.decision.status)} · {fact.decision.note}
                        </p>
                      ) : (
                        <>
                          {choose(fact.id, false, fact.verified && !data.patient_scope_blocked)}
                          <details>
                            <summary>Correct statement from source</summary>
                            <FieldGroup>
                              <Field>
                                <FieldLabel htmlFor={`value-${fact.id}`}>Reviewed statement</FieldLabel>
                                <Textarea
                                  id={`value-${fact.id}`}
                                  value={edits[fact.id]?.value ?? fact.value}
                                  onChange={(e) =>
                                    setEdits((old) => ({
                                      ...old,
                                      [fact.id]: { value: e.target.value, reason: old[fact.id]?.reason || '' },
                                    }))
                                  }
                                />
                              </Field>
                              <Field>
                                <FieldLabel htmlFor={`reason-${fact.id}`}>Correction reason</FieldLabel>
                                <Textarea
                                  id={`reason-${fact.id}`}
                                  value={edits[fact.id]?.reason || ''}
                                  onChange={(e) =>
                                    setEdits((old) => ({
                                      ...old,
                                      [fact.id]: { reason: e.target.value, value: old[fact.id]?.value ?? fact.value },
                                    }))
                                  }
                                />
                              </Field>
                              <Button
                                onClick={() => void correct(fact)}
                                disabled={
                                  busy || processing || !edits[fact.id]?.reason.trim() || !edits[fact.id]?.value.trim()
                                }>
                                Save correction for review
                              </Button>
                            </FieldGroup>
                          </details>
                        </>
                      )}
                    </section>
                  ))}
              </CardContent>
            </Card>
          ))}
          {!data.facts.length && (
            <p>
              No history statements have been staged. Review source coverage and processing issues; this is not a
              negative medical history.
            </p>
          )}
          <details>
            <summary>Page coverage and processing issues</summary>
            <div className="flex flex-col gap-4 pt-3">
              {data.chunks.map((chunk) => (
                <section key={chunk.id} className="flex flex-col gap-2">
                  <strong>
                    Page {chunk.page} · {human(chunk.status)}
                  </strong>
                  {chunk.reason && <p>{causes[chunk.reason] || human(chunk.reason)}</p>}
                  <Button
                    variant="outline"
                    onClick={() =>
                      onSource({ pageNumber: chunk.page, documentId: data.document_id, revisionId: data.revision_id })
                    }>
                    Inspect original page {chunk.page}
                  </Button>
                  <details>
                    <summary>Saved OCR text</summary>
                    <pre className="max-h-64 overflow-auto whitespace-pre-wrap">{chunk.text}</pre>
                  </details>
                  {!chunk.decision && !data.acknowledged && (
                    <details>
                      <summary>Add a manually transcribed statement</summary>
                      <p>
                        Read the original page first. This creates a new proposal with your attribution; it does not
                        resolve failed extraction or apply chart changes.
                      </p>
                      <select
                        value={manualCategory}
                        aria-label="Statement category"
                        onChange={(event) => setManualCategory(event.target.value)}>
                        {[
                          'medical_history',
                          'obstetric_history',
                          'surgical_history',
                          'family_history',
                          'social_history',
                          'medications',
                          'allergies',
                          'imaging_reports',
                          'screening',
                          'consult_notes',
                          'indication',
                          'edd',
                          'lmp',
                          'dating_method',
                          'gestational_age',
                          'requested_service',
                          'requested_timing',
                          'referring_provider',
                        ].map((category) => (
                          <option key={category} value={category}>
                            {human(category)}
                          </option>
                        ))}
                      </select>
                      <Textarea
                        value={edits[chunk.id]?.value || ''}
                        aria-label={`Manual statement page ${chunk.page}`}
                        onChange={(event) =>
                          setEdits((old) => ({
                            ...old,
                            [chunk.id]: { value: event.target.value, reason: old[chunk.id]?.reason || '' },
                          }))
                        }
                      />
                      <Textarea
                        value={edits[chunk.id]?.reason || ''}
                        aria-label={`Transcription reason page ${chunk.page}`}
                        placeholder="Explain what you verified against the original page"
                        onChange={(event) =>
                          setEdits((old) => ({
                            ...old,
                            [chunk.id]: { reason: event.target.value, value: old[chunk.id]?.value || '' },
                          }))
                        }
                      />
                      <Button
                        onClick={() => void correct({ id: chunk.id }, manualCategory)}
                        disabled={
                          busy || processing || !edits[chunk.id]?.value.trim() || !edits[chunk.id]?.reason.trim()
                        }>
                        Stage manual statement for review
                      </Button>
                    </details>
                  )}
                  {chunk.decision ? (
                    <p>
                      {human(chunk.decision.status)} · {chunk.decision.note}
                    </p>
                  ) : (
                    chunk.status === 'failed' && choose(chunk.id, false, false)
                  )}
                </section>
              ))}
            </div>
          </details>
          <details>
            <summary>
              Laboratory review · {human(data.labs.status)} · {data.labs.results_count} candidates
            </summary>
            <LabReview
              primaryStatus="extracted"
              patientId={data.patient_id}
              documentId={data.document_id}
              revisionId={data.revision_id}
            />
            <p>
              After saving reviewed results, refresh record review. Laboratory changes use their separate approval
              controls.
            </p>
            {data.lab_decision ? (
              <p>
                {data.lab_decision.status} · {data.lab_decision.note}
              </p>
            ) : (
              !data.labs.resolved && choose('labs', false, false)
            )}
          </details>
          {!data.acknowledged && (
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor={`record-note-${runId}`}>Reason for rejected or deferred items</FieldLabel>
                <Textarea
                  id={`record-note-${runId}`}
                  value={note}
                  maxLength={4000}
                  onChange={(e) => setNote(e.target.value)}
                />
              </Field>
              <Field orientation="horizontal">
                <Checkbox
                  id={`record-reviewed-${runId}`}
                  checked={reviewed}
                  onCheckedChange={(v) => setReviewed(v === true)}
                />
                <FieldLabel htmlFor={`record-reviewed-${runId}`}>
                  I verified the patient identity, source and selected changes. Existing chart values will be preserved.
                </FieldLabel>
              </Field>
              <Button
                onClick={() => void submit()}
                disabled={
                  busy ||
                  processing ||
                  !!error ||
                  !reviewed ||
                  !data.patient_id ||
                  !Object.values(choices).some(Boolean) ||
                  ((selection('reject').length > 0 || selection('defer').length > 0) && !note.trim())
                }>
                Accept {selection('accept').length} selected changes / save decisions
              </Button>
              <Button
                variant="outline"
                onClick={() => void submit(true)}
                disabled={
                  busy ||
                  processing ||
                  !!error ||
                  !reviewed ||
                  !data.patient_id ||
                  Object.values(choices).some(Boolean) ||
                  data.facts.some((f) => !f.decision) ||
                  data.chunks.some((c) => c.status !== 'complete' && !c.decision) ||
                  (!data.labs.resolved && !data.lab_decision)
                }>
                Acknowledge completed document review
              </Button>
            </FieldGroup>
          )}
          {data.acknowledged && (
            <p>Document review acknowledged. Any deferred sections remain on the patient checklist.</p>
          )}
          {data.patient_id && (
            <a href={`/patients/${data.patient_id}?tab=details`}>Open patient History and referral records</a>
          )}
        </>
      )}
    </section>
  );
}
